import React, { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { getResult, startQuestion } from 'src/apis/gameApi'
import { nextStep, setGameCompleted } from 'src/ducks/game'
import GameBtn from '../GameBtn'
import { AlertCard } from '../styles/mobile-styles'

interface ResultProps {
  onComplete?: () => void
  transitionDuration?: number
}

const DEFAULT_TRANSITION_DURATION = 300

export default function Result({ onComplete, transitionDuration = DEFAULT_TRANSITION_DURATION }: ResultProps) {
  const totalSteps = 13
  const step = useSelector((state: any) => state.game.step)
  const dispatch = useDispatch()
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [frozenResultData, setFrozenResultData] = useState<any>(null)

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  const { data: resultData } = useQuery({
    queryKey: ['fetchResult', 'question', step],
    queryFn: async () => {
      return await getResult(step)
    },
  })

  // Freeze the result data when transitioning to prevent UI flicker
  useEffect(() => {
    if (resultData && !isTransitioning) {
      setFrozenResultData(resultData)
    }
  }, [resultData, isTransitioning])

  // Use frozen data during transitions, otherwise use current data
  const displayData = isTransitioning ? frozenResultData : resultData

  const { mutate } = useMutation({
    mutationFn: async () => {
      return await startQuestion(step + 1)
    },
    onSuccess: () => {
      console.log('Game started successfully')
    },
    onError: (error) => {
      console.error('Error starting the game:', error)
    },
  })

  const handleNextQuestion = () => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
    }

    // Set transitioning state to freeze the UI
    setIsTransitioning(true)

    const scheduleComplete = () => {
      setIsTransitioning(false)
      if (onComplete) {
        onComplete()
      }
    }

    if (step === totalSteps) {
      console.log('Game completed!')
      dispatch(setGameCompleted(true))

      transitionTimeoutRef.current = setTimeout(scheduleComplete, transitionDuration)
      return
    }

    console.log('Proceeding to next question...')
    mutate()
    dispatch(nextStep())

    transitionTimeoutRef.current = setTimeout(scheduleComplete, transitionDuration)
  }

  return (
    <>
      <AlertCard style={{ alignSelf: 'center' }}>
        {displayData ? (
          <>
            {' '}
            <legend>
              <div>Score</div>
            </legend>
            <p className="correct">{displayData.isCorrect ? 'Correct' : 'Incorrect'}</p>
            <div className="d-flex flex-column justify-content-center align-items-center gap-3 flex-wrap">
              {renderIcon(displayData.isCorrect)}

              <h2 className="score">{`+ ${displayData.totalScore}`}</h2>
            </div>
          </>
        ) : (
          <span>Loading...</span>
        )}
      </AlertCard>
      <GameBtn arrow onClick={handleNextQuestion}>
        {step === totalSteps ? 'End Game' : 'Next Question'}
      </GameBtn>
    </>
  )
}

const renderIcon = (isCorrect: boolean = true) => {
  if (isCorrect) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0 40C0 29.3913 4.21427 19.2172 11.7157 11.7157C19.2172 4.21427 29.3913 0 40 0C50.6087 0 60.7828 4.21427 68.2843 11.7157C75.7857 19.2172 80 29.3913 80 40C80 50.6087 75.7857 60.7828 68.2843 68.2843C60.7828 75.7857 50.6087 80 40 80C29.3913 80 19.2172 75.7857 11.7157 68.2843C4.21427 60.7828 0 50.6087 0 40ZM37.7173 57.12L60.7467 28.3307L56.5867 25.0027L36.9493 49.5413L23.04 37.952L19.6267 42.048L37.7173 57.12Z"
          fill="#69CF39"
        />
      </svg>
    )
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
      <path
        d="M40 0C62.12 0 80 17.88 80 40C80 62.12 62.12 80 40 80C17.88 80 0 62.12 0 40C0 17.88 17.88 0 40 0ZM54.36 20L40 34.36L25.64 20L20 25.64L34.36 40L20 54.36L25.64 60L40 45.64L54.36 60L60 54.36L45.64 40L60 25.64L54.36 20Z"
        fill="#FF3333"
      />
    </svg>
  )
}
